/** No zone is more than 14h from UTC, so offsets read this far either side bracket any wall clock. */
const MAX_ZONE_OFFSET_MS = 14 * 3600 * 1000;
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/;
/** Per-zone formatter; null marks a zone the runtime rejects. */
const wallClockFormats = new Map();
/**
 * Instant a wall-clock date-time names in an IANA zone. A time skipped by a DST
 * gap uses the pre-gap offset and a repeated time its first occurrence (RFC 5545).
 *
 * @param value - Local date-time without an offset, e.g. "2026-10-17T16:00:00".
 * @param timeZone - IANA zone name.
 * @returns The instant, or null when the value or zone is unusable.
 */
export function zonedWallClock(value, timeZone) {
    const match = WALL_CLOCK.exec(value ?? "");
    const format = timeZone ? wallClockFormat(timeZone) : null;
    if (!match || !format)
        return null;
    const [year, month, day, hour, minute, second] = match.slice(1).map(Number);
    const wallClockAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
    const offsetBefore = zoneOffsetAt(format, wallClockAsUtc - MAX_ZONE_OFFSET_MS);
    const offsetAfter = zoneOffsetAt(format, wallClockAsUtc + MAX_ZONE_OFFSET_MS);
    if (offsetBefore === offsetAfter)
        return new Date(wallClockAsUtc - offsetBefore);
    // The larger offset gives the earlier instant, so the first match is the first occurrence.
    const offsets = [Math.max(offsetBefore, offsetAfter), Math.min(offsetBefore, offsetAfter)];
    const exact = offsets.find((offset) => zoneOffsetAt(format, wallClockAsUtc - offset) === offset);
    return new Date(wallClockAsUtc - (exact ?? Math.min(offsetBefore, offsetAfter)));
}
/**
 * Memoized wall-clock formatter for a zone.
 *
 * @param timeZone - IANA zone name.
 * @returns The formatter, or null when the runtime rejects the zone.
 */
function wallClockFormat(timeZone) {
    if (wallClockFormats.has(timeZone))
        return wallClockFormats.get(timeZone) ?? null;
    let format;
    try {
        format = new Intl.DateTimeFormat("en-US", {
            timeZone,
            hourCycle: "h23",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    }
    catch {
        format = null;
    }
    wallClockFormats.set(timeZone, format);
    return format;
}
/**
 * Milliseconds the zone's wall clock is ahead of UTC at an instant.
 *
 * @param format - Wall-clock formatter for the zone.
 * @param instant - UTC epoch milliseconds.
 * @returns The offset, negative west of UTC.
 */
function zoneOffsetAt(format, instant) {
    const part = {};
    for (const { type, value } of format.formatToParts(new Date(instant)))
        part[type] = value;
    const wallClock = Date.UTC(Number(part.year), Number(part.month) - 1, Number(part.day), 
    // Some engines print midnight as "24" even under hourCycle "h23"; keep it off the slow path.
    Number(part.hour) % 24, Number(part.minute), Number(part.second));
    return wallClock - Math.floor(instant / 1000) * 1000;
}
//# sourceMappingURL=zonedTime.js.map